/**
 * @module ChartScreen
 * @description This module renders a chart displaying the total daily receipt spend.
 * It fetches the daily totals from the backend and displays them using a bar chart.
 */

import React, { useEffect, useState, useContext } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js/auto'
import Loader from '../components/Loader.js'
import Message from '../components/Message.js'
import { getDailyTotals } from '../actions/metaActions.js'
import { ThemeContext } from '../components/ThemeContext.js'

/**
 * @function DailySpendChart
 * @description Component that renders a bar chart of daily total spend.
 * It fetches the daily totals from the backend and displays them using a bar chart.
 * @returns {JSX.Element} The rendered component.
 */
const DailySpendChart = () => {
  const { theme, isDarkMode } = useContext(ThemeContext)
  const dispatch = useDispatch()
  const [oldcount, setOldcount] = useState(0)

  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  // const id = userInfo._id

  const countRcpt = useSelector((state) => state.countRcpt)
  const { countRecpt } = countRcpt

  const metaDailyTotal = useSelector((state) => state.metaDailyTotal)
  const { dailyTotals, loading, error } = metaDailyTotal

  useEffect(() => {
    if (dailyTotals === undefined && userInfo) {
      dispatch(getDailyTotals(userInfo._id))
      setOldcount(countRecpt)
    }
  }, [dispatch, dailyTotals, userInfo, countRecpt])

  useEffect(() => {
    if (dailyTotals && countRecpt !== oldcount && userInfo) {
      dispatch(getDailyTotals(userInfo._id))
    }
  }, [dispatch, dailyTotals, userInfo, countRecpt, oldcount])

  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
  )

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: theme.text }
      },
      title: {
        display: true,
        text: 'Daily Total Spend',
        color: theme.text
      },
      font: {
        size: 18
      }
    },
    scales: {
      x: {
        ticks: { color: theme.subtext },
        grid: { color: isDarkMode ? '#2D342D' : '#DFE6E9' }
      },
      y: {
        ticks: { color: theme.subtext },
        grid: { color: isDarkMode ? '#2D342D' : '#DFE6E9' }
      }
    }
  }

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <Button
              as={Link}
              to='/reports'
              className='btn btn-sm my-2'
              variant='outline-primary'
              style={{
                borderColor: theme.accent,
                color: theme.accent,
                backgroundColor: 'transparent'
              }}
            >
              Back to Reports
            </Button>
            {loading && <Loader />}
            {error && <Message variant='danger'>{error}</Message>}
            {dailyTotals && (
              <Bar
                data={{
                  labels: dailyTotals.datearray,
                  datasets: [
                    {
                      label: 'Spend ($)',
                      backgroundColor: 'rgba(75,192,192,0.2)',
                      borderColor: 'rgba(75,192,192,1)',
                      borderWidth: 2,
                      hoverBackgroundColor: 'rgba(75,192,192,0.4)',
                      hoverBorderColor: 'rgba(75,192,192,1)',
                      data: dailyTotals.moneyarray
                    }
                  ]
                }}
                options={options}
              />
            )}
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default DailySpendChart
